import type { WebsiteEventGoal } from 'types';
import type { EventGoalRole } from '@/state/useWebsiteDetails';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchInput } from '@/components/SearchInput';
import { Target, Ban, LineChart, MoreHorizontal } from 'lucide-react';
import { cn } from 'helpers/cn';
import { useEventGoalsMapping, type EventGoalsBucket } from './useEventGoalsMapping';

type EventGoalsConfigProps = {
  websiteId: number;
};

type Zone = {
  role: EventGoalRole;
  title: string;
  description: string;
  empty: string;
  icon: typeof Target;
  iconClass: string;
  /** Applied to the whole column while a goal is dragged over it. */
  dropClass: string;
  /** Applied to each goal chip in the column. */
  chipClass: string;
};

/**
 * Eligibility, not selection. A website declares which of the events it emits may be
 * judged on and which are worth watching; each experiment then picks its own primary from
 * the first pool and its secondaries from the second. That is why more than one event can
 * sit in Primary — this is not "the" objective.
 *
 * Console semantics: a primary objective is the thing that decides a result, so it takes
 * the accent; secondaries are a selection of supporting evidence and take `sel`; unmapped
 * events are neither and stay on the plain hairline.
 */
const ZONES: Zone[] = [
  {
    role: 'primary',
    title: 'Primary objectives',
    description: 'Events an experiment may be judged on. One of these decides each result.',
    empty: 'Drag the events that represent a real conversion here.',
    icon: Target,
    iconClass: 'text-accent',
    dropClass: 'border-accent-line bg-panel2',
    chipClass: 'border-accent-line text-accent',
  },
  {
    role: 'secondary',
    title: 'Secondary objectives',
    description: 'Shown alongside results as supporting evidence. Never decides a verdict.',
    empty: 'Drag events that help explain a result here, like add to cart.',
    icon: LineChart,
    iconClass: 'text-sel',
    dropClass: 'border-sel-line bg-sel-bg',
    chipClass: 'border-sel-line text-sel',
  },
  {
    role: 'ignored',
    title: 'Not mapped',
    description: 'Still recorded, just not reported. Move one out whenever it becomes useful.',
    empty: 'Newly discovered events arrive here.',
    icon: Ban,
    iconClass: 'text-dim',
    dropClass: 'border-line bg-panel2',
    chipClass: 'border-line text-dim',
  },
];

type EventGoalsManager = ReturnType<typeof useEventGoalsMapping>;

type GoalBadgeProps = {
  goal: WebsiteEventGoal;
  zone: Zone;
  manager: EventGoalsManager;
};

const GoalBadge = ({ goal, zone, manager }: GoalBadgeProps) => {
  const Icon = zone.icon;
  const name = goal.name ?? `Event ${goal.id}`;

  return (
    <div
      className={cn(
        'micro-label rounded-pill flex max-w-full cursor-grab items-center gap-1.5 border py-1.5 pl-3 pr-1',
        'active:cursor-grabbing',
        zone.chipClass,
      )}
      draggable
      onDragStart={(ev) => manager.handleDragStart(ev, goal)}
    >
      {zone.role !== 'ignored' && <Icon className="h-3 w-3 shrink-0" />}
      <span className="truncate">{name}</span>
      {/*
        Dragging is not the only way to map an event. It was, briefly, and
        that left keyboard users no route at all and made the mapping
        impossible to cover in a test.
      */}
      <DropdownMenu>
        <DropdownMenuTrigger
          className="hover:bg-hover shrink-0 rounded-full p-0.5"
          aria-label={`Change role of ${name}`}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {ZONES.filter((target) => target.role !== goal.role).map((target) => (
            <DropdownMenuItem key={target.role} onSelect={() => manager.updateEventGoalRole(goal.id, target.role)}>
              Move to {target.title.toLowerCase()}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

type ZoneCardProps = {
  zone: Zone;
  bucket: EventGoalsBucket;
  manager: EventGoalsManager;
};

const ZoneCard = ({ zone, bucket, manager }: ZoneCardProps) => {
  const Icon = zone.icon;
  const isDragOver = manager.dragOverRole === zone.role;

  return (
    <div
      className={cn(
        'bg-panel border-line rounded-panel flex flex-col border transition-colors duration-150',
        isDragOver && zone.dropClass,
      )}
      onDragOver={manager.handleDragOver}
      onDragEnter={() => manager.setDragOverRole(zone.role)}
      onDragLeave={manager.handleDragLeave}
      onDrop={(ev) => manager.handleDrop(ev, zone.role)}
    >
      <div className="border-line shrink-0 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4 shrink-0', zone.iconClass)} />
          <h3 className="text-foreground m-0 text-body font-medium">{zone.title}</h3>
          <span className="micro-label border-line rounded-pill ml-auto border px-2 py-0.5">
            {manager.isSearching ? `${bucket.matchCount} / ${bucket.totalCount}` : bucket.totalCount}
          </span>
        </div>
        <p className="supporting m-0 mt-1">{zone.description}</p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
        {/*
          A column scrolls on its own rather than growing. Without this a website with
          thousands of events stretched the dialog past the viewport and pushed its
          buttons out of reach.
        */}
        <div className="flex max-h-[50vh] min-h-[100px] flex-wrap content-start gap-2 overflow-y-auto pr-1">
          <ZoneBody zone={zone} bucket={bucket} manager={manager} />
        </div>
        {bucket.isCapped && (
          <p className="supporting m-0 shrink-0">
            Showing {bucket.goals.length} of {bucket.matchCount} — refine your search to reach the rest.
          </p>
        )}
      </div>
    </div>
  );
};

const ZoneBody = ({ zone, bucket, manager }: ZoneCardProps) => {
  if (manager.isLoading) return <p className="supporting m-0">Loading events…</p>;
  if (bucket.totalCount === 0) return <p className="supporting m-0">{zone.empty}</p>;
  if (bucket.matchCount === 0) return <p className="supporting m-0">No event here matches your search.</p>;

  return (
    <>
      {bucket.goals.map((goal) => (
        <GoalBadge key={goal.id} goal={goal} zone={zone} manager={manager} />
      ))}
    </>
  );
};

export const EventGoalsConfig = ({ websiteId }: EventGoalsConfigProps) => {
  const manager = useEventGoalsMapping(websiteId);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[14px]">
      {/* `relative`: SearchInput drops its field in absolutely below `md`, so without a
          positioned ancestor here it lands wherever the next one happens to be. */}
      <div className="bg-panel border-line rounded-panel relative flex shrink-0 flex-wrap items-center justify-between gap-2 border px-4 py-3">
        <SearchInput
          className="w-full sm:w-80"
          value={manager.searchInput}
          handleSearch={manager.setSearchInput}
          placeholder="Search events"
        />
        <p className="micro-label m-0">{manager.totalCount} events tracked</p>
      </div>
      {/*
        Whatever the columns end up being, this is the region that absorbs the overflow —
        it can never push the dialog's footer off screen because it is bounded by the
        space left over, not by its content.
      */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-[14px] overflow-y-auto lg:grid-cols-3">
        {ZONES.map((zone) => (
          <ZoneCard key={zone.role} zone={zone} bucket={manager.buckets[zone.role]} manager={manager} />
        ))}
      </div>
    </div>
  );
};
